import { useParams, Link } from "react-router-dom";
import { articles } from "@/mocks/articles";
import { useEffect } from "react";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://acbuy2026.net";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    const existingScript = document.getElementById("article-jsonld");
    if (existingScript) existingScript.remove();

    if (!article) return;

    const articleUrl = `${SITE_URL}/articles/${article.slug}`;
    const jsonLd = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": `${articleUrl}#article`,
          "headline": article.title,
          "description": article.excerpt,
          "url": articleUrl,
          "datePublished": article.date,
          "dateModified": article.date,
          "author": {
            "@type": "Organization",
            "name": "ACBuy Spreadsheet 2026",
            "url": SITE_URL
          },
          "publisher": {
            "@type": "Organization",
            "name": "ACBuy Spreadsheet 2026",
            "url": SITE_URL,
            "logo": {
              "@type": "ImageObject",
              "url": "https://static.readdy.ai/image/751042991eee80e3d0f5ce87d83ca658/767f58c2250624902df30344d9bb9bad.png"
            }
          },
          "keywords": article.keywords.join(", "),
          "isPartOf": {
            "@id": `${SITE_URL}/#website`
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": articleUrl
          }
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": SITE_URL
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Articles",
              "item": `${SITE_URL}/articles`
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": article.title,
              "item": articleUrl
            }
          ]
        }
      ]
    };

    const script = document.createElement("script");
    script.id = "article-jsonld";
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    // Update page title and meta description dynamically
    document.title = `${article.title} | ACBuy Spreadsheet 2026`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", article.excerpt);
    const metaCanonical = document.querySelector('link[rel="canonical"]');
    if (metaCanonical) metaCanonical.setAttribute("href", articleUrl);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", `${article.title} | ACBuy Spreadsheet 2026`);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", article.excerpt);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", articleUrl);

    return () => {
      const s = document.getElementById("article-jsonld");
      if (s) s.remove();
      // Restore defaults
      document.title = "ACBuy Spreadsheet 2026 | Best Hipobuy Spreadsheet Alternative | acbuy2026.net";
      if (metaDesc) metaDesc.setAttribute("content", "ACBuy Spreadsheet 2026 — the most comprehensive replica shopping database. Better than hipobuy spreadsheet 2026. 100,000+ verified products, QC photos, hipobuy shipping coupons, haul guides and more.");
      if (metaCanonical) metaCanonical.setAttribute("href", `${SITE_URL}/`);
      if (ogTitle) ogTitle.setAttribute("content", "ACBuy Spreadsheet 2026 | Best Hipobuy Spreadsheet Alternative");
      if (ogDesc) ogDesc.setAttribute("content", "The most comprehensive ACBuy Spreadsheet 2026 database. 100,000+ verified products, QC photos, hipobuy shipping coupons.");
      if (ogUrl) ogUrl.setAttribute("content", `${SITE_URL}/`);
    };
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Article Not Found</h1>
        <Link to="/" className="text-amber-500 hover:underline cursor-pointer">← Back to Home</Link>
      </div>
    );
  }

  const related = articles.filter((a) => a.id !== article.id).slice(0, 4);

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <img src="https://static.readdy.ai/image/751042991eee80e3d0f5ce87d83ca658/767f58c2250624902df30344d9bb9bad.png" alt="ACBuy2026" className="h-8 w-auto object-contain" />
          </Link>
          <div className="flex items-center gap-3">
            <a href="https://repsw2c.com/" target="_blank" rel="nofollow noopener noreferrer" className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-md whitespace-nowrap cursor-pointer transition-colors">Buy It Now</a>
          </div>
        </div>
      </nav>

      <div className="pt-14">
        {/* Hero */}
        <div className="bg-amber-50 py-12 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <Link to="/" className="inline-flex items-center gap-1 text-amber-600 text-sm font-medium hover:underline cursor-pointer mb-6">
              <i className="ri-arrow-left-line"></i> Back to ACBuy Spreadsheet 2026
            </Link>
            <div className="flex flex-wrap gap-2 mb-4">
              {article.keywords.map((kw) => (
                <span key={kw} className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">{kw}</span>
              ))}
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {article.title}
            </h1>
            <p className="text-gray-600 text-base leading-relaxed mb-4">{article.excerpt}</p>
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <span><i className="ri-calendar-line mr-1"></i>{article.date}</span>
              <span><i className="ri-time-line mr-1"></i>{article.readTime}</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-12">
          <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed">
            {article.content.split("\n\n").map((block, i) => {
              if (block.startsWith("## ")) {
                return <h2 key={i} className="text-xl font-bold text-gray-900 mt-8 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{block.replace("## ", "")}</h2>;
              }
              if (block.startsWith("### ")) {
                return <h3 key={i} className="text-base font-bold text-gray-800 mt-5 mb-2">{block.replace("### ", "")}</h3>;
              }
              if (block.startsWith("- ")) {
                return (
                  <ul key={i} className="list-none space-y-1 mb-4">
                    {block.split("\n").map((line, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                        <i className="ri-checkbox-circle-fill text-amber-500 mt-0.5 shrink-0"></i>
                        <span>{line.replace("- ", "")}</span>
                      </li>
                    ))}
                  </ul>
                );
              }
              if (/^\d+\./.test(block)) {
                return (
                  <ol key={i} className="list-decimal list-inside space-y-1 mb-4 text-sm text-gray-600">
                    {block.split("\n").map((line, j) => <li key={j}>{line.replace(/^\d+\.\s/, "")}</li>)}
                  </ol>
                );
              }
              return <p key={i} className="text-sm text-gray-600 leading-relaxed mb-4">{block}</p>;
            })}
          </div>

          {/* CTA */}
          <div className="mt-12 bg-amber-50 rounded-lg p-6 text-center border border-amber-100">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Ready to Start Shopping?</h3>
            <p className="text-sm text-gray-600 mb-4">Access the full ACBuy Spreadsheet 2026 with 100,000+ verified products.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="https://repsw2c.com/" target="_blank" rel="nofollow noopener noreferrer" className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-md text-sm whitespace-nowrap cursor-pointer transition-colors">
                acbuy2026spreadsheet shipping
              </a>
              <a href="https://repsw2c.com/" target="_blank" rel="nofollow noopener noreferrer" className="px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-md text-sm whitespace-nowrap cursor-pointer transition-colors">
                Buy It Now
              </a>
            </div>
          </div>

          {/* Related */}
          <div className="mt-12">
            <h3 className="text-lg font-bold text-gray-900 mb-5">Related ACBuy Spreadsheet Articles</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {related.map((a) => (
                <Link key={a.id} to={`/articles/${a.slug}`} className="border border-gray-100 rounded-lg p-4 hover:border-amber-300 transition-colors cursor-pointer group">
                  <h4 className="text-sm font-semibold text-gray-800 group-hover:text-amber-600 transition-colors line-clamp-2 mb-1">{a.title}</h4>
                  <p className="text-xs text-gray-400">{a.readTime} · {a.date}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-amber-900 text-white py-8 mt-8">
          <div className="max-w-7xl mx-auto px-4 md:px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-amber-300 text-xs">© 2026 acbuy2026.net — ACBuy Spreadsheet 2026</p>
            <a href="https://repsw2c.com/" target="_blank" rel="nofollow noopener noreferrer" className="text-amber-300 hover:text-white text-xs underline cursor-pointer">Visit repsw2c.com</a>
          </div>
        </footer>
      </div>
    </div>
  );
}
